import React, { useEffect, useState, useMemo, useRef } from "react";
import { Loader2, Save, RotateCcw, FileEdit, Plus, X, Eye, EyeOff, CheckCircle2, Upload } from "lucide-react";
import { adminApi } from "../../api/auth";
import { useContent, useContentRefresh } from "../../context/ContentContext";
import { Input } from "../../components/ui/input";
import MD from "../../components/Markdown";

import { CONTENT_SCHEMAS as SCHEMAS } from "../../adminContentSchemas";

const inputClass = "bg-[#1a1610] border border-[#3a3328] text-white text-sm rounded px-2 py-1.5 w-full focus:border-[#d4a843] focus:outline-none";

function JsonEditor({ value, onChange }) {
  const [text, setText] = React.useState(typeof value === "string" ? value : JSON.stringify(value ?? [], null, 2));
  const [err, setErr] = React.useState("");
  React.useEffect(() => {
    setText(typeof value === "string" ? value : JSON.stringify(value ?? [], null, 2));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return (
    <div>
      <textarea rows={6} className={inputClass + " font-mono text-xs"}
        value={text}
        onChange={(e) => {
          setText(e.target.value);
          try { onChange(JSON.parse(e.target.value)); setErr(""); }
          catch { setErr("JSON invalido"); }
        }} />
      {err && <div className="text-xs text-red-400 mt-1">{err}</div>}
    </div>
  );
}

function ImageField({ value, onChange }) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [err, setErr] = useState("");
  const v = value ?? "";

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setUploading(true);
    setErr("");
    try {
      const data = await adminApi.upload(file);
      onChange(data.url);
    } catch (ex) {
      setErr(ex.message || "Error al subir");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="space-y-2">
      <div className="flex flex-col sm:flex-row gap-2">
        <Input
          className={inputClass}
          value={v}
          onChange={(e) => onChange(e.target.value)}
          placeholder="URL o subí un archivo…"
        />
        <button
          type="button"
          disabled={uploading}
          onClick={() => inputRef.current?.click()}
          className="shrink-0 inline-flex items-center justify-center gap-1.5 px-3 py-1.5 text-xs border border-[#3a3328] rounded text-[#d4a843] hover:border-[#d4a843] disabled:opacity-50"
        >
          {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
          {uploading ? "Subiendo…" : "Subir imagen"}
        </button>
        <input ref={inputRef} type="file" accept="image/jpeg,image/png,image/webp,image/gif" className="hidden" onChange={onFile} />
      </div>
      {err && <div className="text-xs text-red-400">{err}</div>}
      {v ? (
        <div className="flex items-start gap-3">
          <div className="w-28 h-20 rounded border border-[#3a3328] bg-[#0a0a0d] overflow-hidden flex items-center justify-center">
            <img src={v} alt="" className="max-w-full max-h-full object-contain" />
          </div>
          <button type="button" onClick={() => onChange("")} className="text-[10px] text-[#7e7768] hover:text-red-400">
            Quitar
          </button>
        </div>
      ) : null}
    </div>
  );
}

// ============================================================
function FieldEditor({ field, value, onChange }) {
  const v = value ?? "";
  if (field.type === "textarea") {
    return <textarea rows={3} className={inputClass} value={v} onChange={(e) => onChange(e.target.value)} />;
  }
  if (field.type === "markdown") {
    return (
      <div>
        <textarea rows={8} className={inputClass + " font-mono text-xs"} value={v} onChange={(e) => onChange(e.target.value)} placeholder="Markdown soportado: **negrita**, _italica_, [link](url), - listas..." />
        <MarkdownPreview value={v} />
      </div>
    );
  }
  if (field.type === "bool") {
    return (
      <button type="button" onClick={() => onChange(!v)}
        className={`px-3 py-1 rounded text-xs ${v ? "bg-green-900/40 text-green-400" : "bg-[#1a1610] text-[#7e7768]"}`}>
        {v ? "Si" : "No"}
      </button>
    );
  }
  if (field.type === "select") {
    return (
      <select className={inputClass} value={v} onChange={(e) => onChange(e.target.value)}>
        <option value="" disabled>— Elegí una opción —</option>
        {(field.options || []).map((o) => (
          <option key={o.value} value={o.value}>{o.label}</option>
        ))}
      </select>
    );
  }
  if (field.type === "json") {
    return <JsonEditor value={value} onChange={onChange} />;
  }
  if (field.type === "image") {
    return <ImageField value={v} onChange={onChange} />;
  }
  return <Input className={inputClass} value={v} onChange={(e) => onChange(e.target.value)} />;
}

function MarkdownPreview({ value }) {
  const [show, setShow] = useState(false);
  if (!value) return null;
  return (
    <div className="mt-2">
      <button type="button" onClick={() => setShow(!show)}
        className="text-[10px] text-[#7e7768] hover:text-[#d4a843] uppercase tracking-wider inline-flex items-center gap-1">
        {show ? <EyeOff className="w-3 h-3" /> : <Eye className="w-3 h-3" />} {show ? "Ocultar" : "Mostrar"} preview
      </button>
      {show && (
        <div className="mt-2 p-3 border border-[#3a3328] bg-[#0a0a0d] rounded">
          <MD>{value}</MD>
        </div>
      )}
    </div>
  );
}

// ============================================================
function BlockEditor({ block, onSaved }) {
  const schema = SCHEMAS[block.key];
  const [val, setVal] = useState(block.value ?? block.default);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(null);
  const [error, setError] = useState("");

  if (!schema) {
    return <div className="text-[#7e7768] text-sm p-3">Sin schema para {block.key}</div>;
  }

  const save = async () => {
    setSaving(true); setError("");
    try {
      await adminApi.saveContent(block.key, val);
      setSavedAt(Date.now());
      onSaved && onSaved();
    } catch (e) { setError(e.message); }
    finally { setSaving(false); }
  };

  const reset = async () => {
    if (!confirm("Volver al valor por defecto?")) return;
    setSaving(true); setError("");
    try {
      await adminApi.resetContent(block.key);
      setVal(block.default);
      onSaved && onSaved();
    } catch (e) { setError(e.message); }
    finally { setSaving(false); }
  };

  // === LIST type ===
  if (schema.type === "list") {
    const items = Array.isArray(val) ? val : [];
    const setItem = (idx, k, v) => {
      const next = [...items];
      next[idx] = { ...next[idx], [k]: v };
      setVal(next);
    };
    const addItem = () => {
      const empty = {};
      schema.itemFields.forEach((f) => { empty[f.key] = f.type === "bool" ? false : ""; });
      setVal([...items, empty]);
    };
    const removeItem = (idx) => setVal(items.filter((_, i) => i !== idx));
    const moveItem = (idx, dir) => {
      const next = [...items];
      const newIdx = idx + dir;
      if (newIdx < 0 || newIdx >= next.length) return;
      [next[idx], next[newIdx]] = [next[newIdx], next[idx]];
      setVal(next);
    };
    return (
      <div className="space-y-3">
        {items.map((it, idx) => (
          <div key={idx} className="border border-[#3a3328] bg-[#0f0d09] rounded p-3 space-y-2">
            <div className="flex items-center justify-between text-xs">
              <span className="text-[#d4a843]">#{idx + 1}</span>
              <div className="flex gap-1">
                <button type="button" onClick={() => moveItem(idx, -1)} className="text-[#7e7768] hover:text-white px-2 py-0.5">↑</button>
                <button type="button" onClick={() => moveItem(idx, 1)} className="text-[#7e7768] hover:text-white px-2 py-0.5">↓</button>
                <button type="button" onClick={() => removeItem(idx)} className="text-red-400 hover:text-red-300 px-2 py-0.5"><X className="w-3 h-3" /></button>
              </div>
            </div>
            {schema.itemFields.map((f) => (
              <div key={f.key}>
                <label className="text-[10px] uppercase tracking-wider text-[#7e7768] mb-0.5 block">{f.label}</label>
                <FieldEditor field={f} value={it?.[f.key]} onChange={(v) => setItem(idx, f.key, v)} />
              </div>
            ))}
          </div>
        ))}
        <button type="button" onClick={addItem}
          className="w-full py-2 border border-dashed border-[#3a3328] text-[#7e7768] hover:border-[#d4a843] hover:text-[#d4a843] text-xs rounded inline-flex items-center justify-center gap-1">
          <Plus className="w-3 h-3" /> Agregar item
        </button>
        <SaveBar saving={saving} savedAt={savedAt} error={error} onSave={save} onReset={reset} isOverridden={block.is_overridden} />
      </div>
    );
  }

  // === MARKDOWN type ===
  if (schema.type === "markdown") {
    return (
      <div className="space-y-3">
        <FieldEditor field={{ type: "markdown" }} value={val} onChange={setVal} />
        <SaveBar saving={saving} savedAt={savedAt} error={error} onSave={save} onReset={reset} isOverridden={block.is_overridden} />
      </div>
    );
  }
  if (schema.type === "textarea") {
    return (
      <div className="space-y-3">
        <FieldEditor field={{ type: "textarea" }} value={val} onChange={setVal} />
        <SaveBar saving={saving} savedAt={savedAt} error={error} onSave={save} onReset={reset} isOverridden={block.is_overridden} />
      </div>
    );
  }
  if (schema.type === "text") {
    return (
      <div className="space-y-3">
        <FieldEditor field={{ type: "text" }} value={val} onChange={setVal} />
        <SaveBar saving={saving} savedAt={savedAt} error={error} onSave={save} onReset={reset} isOverridden={block.is_overridden} />
      </div>
    );
  }

  // === fields type ===
  const obj = val && typeof val === "object" ? val : {};
  const setObj = (k, v) => setVal({ ...obj, [k]: v });
  return (
    <div className="space-y-3">
      {schema.fields.map((f) => (
        <div key={f.key}>
          <label className="text-[10px] uppercase tracking-wider text-[#7e7768] mb-1 block">{f.label}</label>
          {f.hint && <p className="text-[10px] text-[#9a9588] mb-1.5 leading-relaxed">{f.hint}</p>}
          <FieldEditor field={f} value={obj[f.key]} onChange={(v) => setObj(f.key, v)} />
        </div>
      ))}
      <SaveBar saving={saving} savedAt={savedAt} error={error} onSave={save} onReset={reset} isOverridden={block.is_overridden} />
    </div>
  );
}

function SaveBar({ saving, savedAt, error, onSave, onReset, isOverridden }) {
  return (
    <div className="flex items-center justify-between flex-wrap gap-2 pt-3 border-t border-[#3a3328]">
      <div className="text-xs">
        {error && <span className="text-red-400">{error}</span>}
        {savedAt && <span className="text-green-400 inline-flex items-center gap-1"><CheckCircle2 className="w-3 h-3" /> Guardado</span>}
      </div>
      <div className="flex gap-2">
        {isOverridden && (
          <button type="button" onClick={onReset} disabled={saving}
            className="inline-flex items-center gap-1 px-3 py-1.5 text-xs text-[#7e7768] hover:text-[#d4a843] border border-[#3a3328] rounded">
            <RotateCcw className="w-3 h-3" /> Resetear default
          </button>
        )}
        <button type="button" onClick={onSave} disabled={saving}
          className="inline-flex items-center gap-1.5 px-4 py-1.5 text-xs bg-[#d4a843] text-black font-medium rounded hover:bg-[#e0b860] disabled:opacity-50">
          {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />} Guardar
        </button>
      </div>
    </div>
  );
}

// ============================================================
export default function AdminContent() {
  const refreshGlobalContent = useContentRefresh();
  const [blocks, setBlocks] = useState([]);
  const [storage, setStorage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [openKey, setOpenKey] = useState(null);

  const load = async () => {
    setLoading(true); setError("");
    try {
      const res = await adminApi.content();
      setBlocks(res?.blocks ?? []);
      setStorage(res?.storage ?? null);
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const groups = useMemo(() => {
    const g = {};
    blocks.forEach((b) => {
      const grp = SCHEMAS[b.key]?.group || "Otros";
      if (!g[grp]) g[grp] = [];
      g[grp].push(b);
    });
    return g;
  }, [blocks]);

  return (
    <div className="space-y-4" data-testid="admin-content">
      <div className="flex items-center justify-between">
        <div>
          <div className="ornament"><span>Contenido del sitio</span></div>
          <h1 className="section-title mt-3 text-3xl flex items-center gap-2">
            <FileEdit className="w-7 h-7 text-[#d4a843]" /> Editar Contenido
          </h1>
          <p className="text-sm text-[#b8b3a8] mt-2">Modifica textos, links, descripciones y bloques visibles del sitio publico.</p>
        </div>
      </div>

      {error && <div className="text-red-400 text-sm border border-red-900/40 p-3 rounded">{error}</div>}
      {storage && !storage.file_writable && (
        <div className="text-amber-300 text-sm border border-amber-900/40 bg-amber-950/20 p-3 rounded">
          La carpeta <code className="text-amber-200">api/data/</code> no tiene permiso de escritura. Los cambios del CMS pueden perderse al restaurar la base del gameserver. En cPanel pon permisos 755 o 775 en <code className="text-amber-200">api/data/</code>.
        </div>
      )}
      {storage?.file_exists && storage?.override_count > 0 && (
        <div className="text-green-400/90 text-xs border border-green-900/30 bg-green-950/10 p-2 rounded">
          CMS guardado en el webhost ({storage.override_count} bloques editados). Respaldo automatico en <code>api/data/cms_overrides.bak.json</code>.
        </div>
      )}
      {loading && <Loader2 className="w-6 h-6 animate-spin text-[#d4a843]" />}

      {!loading && Object.entries(groups).map(([grp, items]) => (
        <div key={grp} className="border border-[#3a3328] bg-[#15110a]/70 rounded-lg overflow-hidden">
          <div className="px-4 py-3 bg-black/30 border-b border-[#3a3328]">
            <h3 className="text-xs uppercase tracking-wider text-[#d4a843]">{grp}</h3>
          </div>
          {items.map((b) => (
            <div key={b.key} className="border-b border-[#3a3328]/40 last:border-b-0">
              <button
                type="button"
                onClick={() => setOpenKey(openKey === b.key ? null : b.key)}
                className="w-full px-4 py-3 flex items-center justify-between text-left hover:bg-white/5"
                data-testid={`block-toggle-${b.key}`}
              >
                <div>
                  <div className="text-sm text-white flex items-center gap-2">
                    {SCHEMAS[b.key]?.label || b.key}
                    {b.is_overridden && (
                      <span className="text-[9px] uppercase tracking-wider bg-green-900/40 text-green-300 px-1.5 py-0.5 rounded">editado</span>
                    )}
                  </div>
                  <div className="text-[10px] text-[#7e7768] mt-0.5 font-mono">{b.key}</div>
                  {b.updated_at && (
                    <div className="text-[10px] text-[#7e7768] mt-0.5">
                      por <span className="text-[#d4a843]">{b.updated_by}</span> · {new Date(b.updated_at.replace(' ', 'T')).toLocaleString("es-AR")}
                    </div>
                  )}
                </div>
                <span className="text-[#d4a843]">{openKey === b.key ? "▼" : "▶"}</span>
              </button>
              {openKey === b.key && (
                <div className="p-4 bg-black/20">
                  <BlockEditor block={b} onSaved={() => { load(); refreshGlobalContent && refreshGlobalContent(); }} />
                </div>
              )}
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}
